import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  InputBase,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
  Typography
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import { CloseIcon } from '../../../../constants/icons';
import { CardRarityEnum, CardTypeEnum } from '../../../../types/enums';
import { track } from '../../../../utils/track';

// Arena palette (mirrors the dashboard / collection header).
const C = {
  cyan: '#38e8ff',
  text: '#e8f4fb',
  dim: '#8ea3b3',
  dimmer: '#5c6b78',
  mono: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace'
};

// Scoped dark theme so the MUI controls inside the filter panel (Select,
// TextField, Checkbox, InputLabel and their portalled dropdowns) render on the
// dark surface instead of the app's default light theme.
const filterTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#38e8ff' },
    background: { paper: '#0b1119', default: '#070b11' },
    text: { primary: '#e8f4fb', secondary: '#8ea3b3' }
  }
});

// Mono uppercase kicker used for every field label.
const kickSx = {
  font: `800 10px ${C.mono}`,
  letterSpacing: '0.24em',
  textTransform: 'uppercase',
  color: C.dim,
  mb: '0.6rem'
} as const;

// Selected-value chips in the multi-selects.
const chipSx = {
  height: 24,
  bgcolor: 'rgba(56, 232, 255, 0.12)',
  color: C.cyan,
  border: '1px solid rgba(56, 232, 255, 0.4)',
  fontWeight: 700,
  textTransform: 'capitalize'
} as const;

// Shared cyan button (Show/Hide Filters, Reset Filters).
const cyanBtnSx = {
  fontWeight: 800,
  color: C.cyan,
  backgroundColor: 'rgba(56, 232, 255, 0.12)',
  border: '1px solid rgba(56, 232, 255, 0.4)',
  boxShadow: 'none',
  borderRadius: '10px',
  '&:hover': {
    backgroundColor: 'rgba(56, 232, 255, 0.2)',
    boxShadow: 'none'
  }
} as const;

type NFTFilterProps = {
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  showAdvancedFilters: boolean;
  setShowAdvancedFilters: (value: boolean) => void;
  rarityFilters: string[];
  handleRarityChange: (event: SelectChangeEvent<string[]>) => void;
  typeFilters: string[];
  handleTypeChange: (event: SelectChangeEvent<string[]>) => void;
  factionFilters: string[];
  handleFactionChange: (event: SelectChangeEvent<string[]>) => void;
  setAttackFromFilter: (value: number | null) => void;
  setAttackToFilter: (value: number | null) => void;
  setDefenceFromFilter: (value: number | null) => void;
  setDefenceToFilter: (value: number | null) => void;
  showOwnedCards: boolean;
  setShowOwnedCards: (value: boolean) => void;
  showUnownedCards: boolean;
  setShowUnownedCards: (value: boolean) => void;
  resetFilters: () => void;
  isCreateEditDeckModeEnabled: boolean;
};

export const NFTFilter = ({
  searchQuery,
  setSearchQuery,
  showAdvancedFilters,
  setShowAdvancedFilters,
  rarityFilters,
  handleRarityChange,
  typeFilters,
  handleTypeChange,
  factionFilters,
  handleFactionChange,
  setAttackFromFilter,
  setAttackToFilter,
  setDefenceFromFilter,
  setDefenceToFilter,
  showOwnedCards,
  setShowOwnedCards,
  showUnownedCards,
  setShowUnownedCards,
  resetFilters,
  isCreateEditDeckModeEnabled
}: NFTFilterProps) => {
  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250
      }
    }
  };

  const renderChips = (selected: string[]) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {selected.map((value: string) => (
        <Chip key={value} label={value} sx={chipSx} />
      ))}
    </Box>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'stretch',
          gap: 1.5
        }}
      >
        <Box
          component="form"
          onSubmit={(e) => e.preventDefault()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            // Match the MUI outlined filter fields (56px) so the search sits on
            // the same rhythm as Rarity/Type/Faction/Attack/Defence.
            minHeight: 56,
            px: 1.5,
            borderRadius: '12px',
            background: 'rgba(7, 11, 17, 0.75)',
            border: '1px solid rgba(56, 232, 255, 0.22)',
            backdropFilter: 'blur(6px)',
            transition: 'border-color 120ms, box-shadow 120ms',
            '&:focus-within': {
              borderColor: 'rgba(56, 232, 255, 0.6)',
              boxShadow: '0 0 0 1px rgba(56, 232, 255, 0.35)'
            }
          }}
        >
          <SearchIcon sx={{ color: C.dim, mr: 1 }} />
          <InputBase
            sx={{
              flex: 1,
              color: C.text,
              fontFamily: C.mono,
              '& input::placeholder': { color: C.dimmer, opacity: 1 }
            }}
            placeholder="Search Digital Collectible Cards"
            inputProps={{ 'aria-label': 'search nft cards' }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setSearchQuery(e.currentTarget.value);
                track('collection.search', { q: e.currentTarget.value });
              }
            }}
            onChange={(e) => {
              setSearchQuery(e.currentTarget.value);
            }}
            value={searchQuery}
          />
          <IconButton
            sx={{ p: '8px', color: C.dim, '&:hover': { color: C.cyan } }}
            aria-label="clear search"
            onClick={() => {
              setSearchQuery('');
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Button
          variant="contained"
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          data-track="collection.filters_toggle"
          sx={{ ...cyanBtnSx, whiteSpace: 'nowrap' }}
        >
          {showAdvancedFilters ? 'Hide Filters' : 'Show Filters'}
        </Button>
      </Box>

      <Box
        sx={{
          display: showAdvancedFilters ? 'block' : 'none',
          mt: '1rem',
          width: '100%',
          overflow: 'hidden',
          borderRadius: '16px',
          border: '1px solid rgba(56, 232, 255, 0.22)',
          background:
            'linear-gradient(180deg, rgba(11, 17, 25, 0.86), rgba(7, 11, 17, 0.92))',
          backdropFilter: 'blur(6px)',
          boxShadow: '0 18px 40px -26px rgba(0, 0, 0, 0.9)'
        }}
      >
        <ThemeProvider theme={filterTheme}>
          <Box
            sx={{
              display: 'flex',
              p: '1.25rem',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                gap: '1rem',
                flexWrap: { xs: 'wrap', md: 'nowrap' }
              }}
            >
              <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 0 } }}>
                <Typography sx={kickSx}>Rarity</Typography>
                <FormControl sx={{ width: '100%' }}>
                  <InputLabel id="rarity-label">Select Rarity</InputLabel>
                  <Select
                    labelId="rarity-label"
                    id="rarity-select"
                    multiple
                    value={rarityFilters}
                    onChange={handleRarityChange}
                    input={
                      <OutlinedInput
                        id="select-multiple-rarities"
                        label="Select Rarity"
                      />
                    }
                    renderValue={(selected) => renderChips(selected as string[])}
                    MenuProps={MenuProps}
                  >
                    <MenuItem value={CardRarityEnum.Common}>
                      <Checkbox
                        checked={
                          rarityFilters.indexOf(CardRarityEnum.Common) > -1
                        }
                      />
                      <ListItemText primary={CardRarityEnum.Common} />
                    </MenuItem>
                    <MenuItem value={CardRarityEnum.Rare}>
                      <Checkbox
                        checked={rarityFilters.indexOf(CardRarityEnum.Rare) > -1}
                      />
                      <ListItemText primary={CardRarityEnum.Rare} />
                    </MenuItem>
                    <MenuItem value={CardRarityEnum.Epic}>
                      <Checkbox
                        checked={rarityFilters.indexOf(CardRarityEnum.Epic) > -1}
                      />
                      <ListItemText primary={CardRarityEnum.Epic} />
                    </MenuItem>

                    <MenuItem value={CardRarityEnum.Legendary}>
                      <Checkbox
                        checked={
                          rarityFilters.indexOf(CardRarityEnum.Legendary) > -1
                        }
                      />
                      <ListItemText primary={CardRarityEnum.Legendary} />
                    </MenuItem>
                    <MenuItem value={CardRarityEnum.Relic}>
                      <Checkbox
                        checked={
                          rarityFilters.indexOf(CardRarityEnum.Relic) > -1
                        }
                      />
                      <ListItemText primary={CardRarityEnum.Relic} />
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 0 } }}>
                <Typography sx={kickSx}>Type</Typography>
                <FormControl sx={{ width: '100%' }}>
                  <InputLabel id="type-label">Select Type</InputLabel>
                  <Select
                    labelId="type-label"
                    id="type-select"
                    multiple
                    value={typeFilters}
                    onChange={handleTypeChange}
                    input={
                      <OutlinedInput
                        id="select-multiple-types"
                        label="Select Type"
                      />
                    }
                    renderValue={(selected) => renderChips(selected as string[])}
                    MenuProps={MenuProps}
                    fullWidth
                  >
                    <MenuItem value={CardTypeEnum.Token}>
                      <Checkbox
                        checked={typeFilters.indexOf(CardTypeEnum.Token) > -1}
                      />
                      <ListItemText primary={CardTypeEnum.Token} />
                    </MenuItem>
                    <MenuItem value={CardTypeEnum.TokenCharacter}>
                      <Checkbox
                        checked={
                          typeFilters.indexOf(CardTypeEnum.TokenCharacter) > -1
                        }
                      />
                      <ListItemText primary={CardTypeEnum.TokenCharacter} />
                    </MenuItem>
                    <MenuItem value={CardTypeEnum.Influencer}>
                      <Checkbox
                        checked={
                          typeFilters.indexOf(CardTypeEnum.Influencer) > -1
                        }
                      />
                      <ListItemText primary={CardTypeEnum.Influencer} />
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 0 } }}>
                <Typography sx={kickSx}>Faction</Typography>
                <FormControl sx={{ width: '100%' }}>
                  <InputLabel id="faction-label">Select Faction</InputLabel>
                  <Select
                    labelId="faction-label"
                    id="faction-select"
                    multiple
                    value={factionFilters}
                    onChange={handleFactionChange}
                    input={
                      <OutlinedInput
                        id="select-multiple-factions"
                        label="Select Faction"
                      />
                    }
                    renderValue={(selected) => renderChips(selected as string[])}
                    MenuProps={MenuProps}
                    fullWidth
                  >
                    <MenuItem value="alien">
                      <Checkbox
                        checked={factionFilters.indexOf('alien') > -1}
                      />
                      <ListItemText primary="alien" />
                    </MenuItem>
                    <MenuItem value="cyborg">
                      <Checkbox
                        checked={factionFilters.indexOf('cyborg') > -1}
                      />
                      <ListItemText primary="cyborg" />
                    </MenuItem>
                    <MenuItem value="dragon">
                      <Checkbox
                        checked={factionFilters.indexOf('dragon') > -1}
                      />
                      <ListItemText primary="dragon" />
                    </MenuItem>
                    <MenuItem value="human">
                      <Checkbox
                        checked={factionFilters.indexOf('human') > -1}
                      />
                      <ListItemText primary="human" />
                    </MenuItem>
                    <MenuItem value="incrypt">
                      <Checkbox
                        checked={factionFilters.indexOf('incrypt') > -1}
                      />
                      <ListItemText primary="incrypt" />
                    </MenuItem>
                    <MenuItem value="hybrid">
                      <Checkbox
                        checked={factionFilters.indexOf('hybrid') > -1}
                      />
                      <ListItemText primary="hybrid" />
                    </MenuItem>
                    <MenuItem value="mech">
                      <Checkbox checked={factionFilters.indexOf('mech') > -1} />
                      <ListItemText primary="mech" />
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                width: '100%',
                gap: '1rem',
                mt: '1.25rem',
                flexWrap: { xs: 'wrap', md: 'nowrap' }
              }}
            >
              <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 0 } }}>
                <Typography sx={kickSx}>Attack</Typography>
                <Box sx={{ display: 'flex', gap: '1rem' }}>
                  <TextField
                    label="From"
                    onChange={(e) => {
                      if (parseInt(e.currentTarget.value)) {
                        setAttackFromFilter(parseInt(e.currentTarget.value));
                      } else {
                        setAttackFromFilter(null);
                      }
                    }}
                    variant="outlined"
                    fullWidth
                  />
                  <TextField
                    label="To"
                    onChange={(e) => {
                      if (parseInt(e.currentTarget.value)) {
                        setAttackToFilter(parseInt(e.currentTarget.value));
                      } else {
                        setAttackToFilter(null);
                      }
                    }}
                    variant="outlined"
                    fullWidth
                  />
                </Box>
              </Box>
              <Box sx={{ flex: 1, minWidth: { xs: '100%', md: 0 } }}>
                <Typography sx={kickSx}>Defence</Typography>
                <Box sx={{ display: 'flex', gap: '1rem' }}>
                  <TextField
                    label="From"
                    onChange={(e) => {
                      if (parseInt(e.currentTarget.value)) {
                        setDefenceFromFilter(parseInt(e.currentTarget.value));
                      } else {
                        setDefenceFromFilter(null);
                      }
                    }}
                    variant="outlined"
                    fullWidth
                  />
                  <TextField
                    label="To"
                    onChange={(e) => {
                      if (parseInt(e.currentTarget.value)) {
                        setDefenceToFilter(parseInt(e.currentTarget.value));
                      } else {
                        setDefenceToFilter(null);
                      }
                    }}
                    variant="outlined"
                    fullWidth
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '1rem',
              justifyContent: !isCreateEditDeckModeEnabled
                ? 'space-between'
                : 'end',
              borderTop: '1px solid rgba(56, 232, 255, 0.12)',
              p: '1.25rem'
            }}
          >
            {!isCreateEditDeckModeEnabled && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: '2rem' }}>
                  <Checkbox
                    checked={showOwnedCards}
                    onChange={() => {
                      const next = !showOwnedCards;
                      setShowOwnedCards(next);
                      track('collection.toggle', { kind: 'owned', on: next });
                    }}
                  />
                  <ListItemText
                    primary="Owned Digital Collectibles"
                    primaryTypographyProps={{
                      sx: { font: `600 13px ${C.mono}`, color: C.text }
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    checked={showUnownedCards}
                    onChange={() => {
                      const next = !showUnownedCards;
                      setShowUnownedCards(next);
                      track('collection.toggle', {
                        kind: 'unowned',
                        on: next
                      });
                    }}
                  />
                  <ListItemText
                    primary="Unowned Digital Collectibles"
                    primaryTypographyProps={{
                      sx: { font: `600 13px ${C.mono}`, color: C.text }
                    }}
                  />
                </Box>
              </Box>
            )}
            <Button
              variant="contained"
              onClick={resetFilters}
              size="large"
              data-track="collection.filter_reset_click"
              sx={cyanBtnSx}
            >
              Reset Filters
            </Button>
          </Box>
        </ThemeProvider>
      </Box>
    </Box>
  );
};
